import OktaAuth, { toRelativeUrl } from '@okta/okta-auth-js';
import { Security } from '@okta/okta-react';
import { useState } from 'react';
import { I18nextProvider } from 'react-i18next';
import { Provider } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { store } from './app/store';
import { AppContext } from './contexts/appContextProvider';
import { AlertProvider } from './lib/AlertContext';
import { CypressSecurityMock } from './lib/CypressSecurityMock';
import { getOktaConfig } from './oktaConfig';

import { FeatureFlagProvider } from './lib/featureFlags/FeatureFlagProvider';
import { AppRouter } from './router/AppRouter';
import i18nAppInstance from './translations/i18n';

const oktaAuth = new OktaAuth(getOktaConfig(window));

// Expose store for Cypress
if ((window as any).Cypress) {
    (window as any).store = store;
}

export const App = (): JSX.Element => {
    const [showChangePasswordDialog, setShowChangePasswordDialog] = useState(false);

    const navigate = useNavigate();

    const restoreOriginalUri = (_oktaAuth: OktaAuth, originalUri: string): void => {
        navigate(toRelativeUrl(originalUri || '/', window.location.origin));
    };

    const isCypress = !!(window as any).Cypress && !!(window as any).cypressForceAuth;

    const appContent = (
        <AppContext.Provider
            value={{
                showChangePasswordDialog,
                setShowChangePasswordDialog,
            }}
        >
            <I18nextProvider i18n={i18nAppInstance}>
                <AlertProvider>
                    <Provider store={store}>
                        <FeatureFlagProvider>
                            <AppRouter />
                        </FeatureFlagProvider>
                    </Provider>
                </AlertProvider>
            </I18nextProvider>
        </AppContext.Provider>
    );

    if (isCypress) {
        return <CypressSecurityMock>{appContent}</CypressSecurityMock>;
    }

    return (
        <AppContext.Provider
            value={{
                showChangePasswordDialog,
                setShowChangePasswordDialog,
            }}
        >
            <I18nextProvider i18n={i18nAppInstance}>
                <AlertProvider>
                    <Provider store={store}>
                        <Security oktaAuth={oktaAuth} restoreOriginalUri={restoreOriginalUri}>
                            <FeatureFlagProvider>
                                <AppRouter />
                            </FeatureFlagProvider>
                        </Security>
                    </Provider>
                </AlertProvider>
            </I18nextProvider>
        </AppContext.Provider>
    );
};
